import { Text, Flex, Button } from '@components';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { MessageContainer } from './ExternalContractSigning.styles';
import LocalLoader from '@components/AppLoader/LocalLoader';
import {
  MAX_RETRIES,
  RETRIABLE_STATUS_CODES,
  RETRY_INTERVAL_MS,
} from '@pages/Investment/ContractSigning/ContractSigning';
import {
  COMPLETED_EVENT,
  ENVELOPE_EXPIRED,
} from '@pages/Investment/ContractSigning/ContractSigning.const';
import { useGetExternalContractQuery } from '@features/api/investmentSlice';

const ExternalContractSigning = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const [isRetrying, setIsRetrying] = useState(false);
  const [timesRefetched, setTimesRefetched] = useState(0);
  const [showComeBackLater, setShowComeBackLater] = useState(false);
  const [iFrameLoaded, setIFrameLoaded] = useState(false);
  const [isSigningComplete, setIsSigningComplete] = useState(false);

  const { data, error, isFetching, isLoading, refetch } =
    useGetExternalContractQuery(
      { token: token ?? '' },
      {
        refetchOnMountOrArgChange: true,
        skip: !token || token === '',
      }
    );

  const { url: docusignUrl } = data ?? {};
  useEffect(() => {
    setIFrameLoaded(false);
  }, [docusignUrl]);

  useEffect(() => {
    const typedError = error as any;
    const shouldRefetch =
      RETRIABLE_STATUS_CODES.includes(typedError?.status) &&
      timesRefetched < MAX_RETRIES;
    if (shouldRefetch) {
      setIsRetrying(true);
      const id = setTimeout(() => {
        if (shouldRefetch) {
          refetch();
          setTimesRefetched((timesRefetched) => timesRefetched + 1);
        }
      }, RETRY_INTERVAL_MS);

      return () => clearTimeout(id);
    } else if (
      RETRIABLE_STATUS_CODES.includes(typedError?.status) &&
      timesRefetched >= MAX_RETRIES
    ) {
      setShowComeBackLater(true);
    } else if (!error) {
      setIsRetrying(false);
      setShowComeBackLater(false);
      setTimesRefetched(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    const handler = (ev: MessageEvent<{ type: string; message: string }>) => {
      if (ev.data.message === COMPLETED_EVENT) {
        setIsSigningComplete(true);
      } else if (ev.data.message === ENVELOPE_EXPIRED) {
        refetch();
      }
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isSigningComplete) {
    return (
      <MessageContainer padding='20%'>
        <Text variant='h5' fontWeight='fontWeightMedium'>
          <FormattedMessage id='investment.contractSigning.signingComplete' />
        </Text>
      </MessageContainer>
    );
  }

  const showLoader = isFetching || isLoading || !iFrameLoaded;
  if (showComeBackLater) {
    return (
      <MessageContainer padding='20%'>
        <Text variant='h5' fontWeight='fontWeightMedium'>
          <FormattedMessage id='investment.contractSigning.pleaseComeBackLater' />
        </Text>
      </MessageContainer>
    );
  }

  if (error || !token) {
    if (
      (error as any)?.data?.detail === 'PASSWORD RESET REQUEST EXPIRED' ||
      (error as any)?.data?.detail ===
        'PASSWORD RESET REQUEST DOES NOT EXIST' ||
      (error as any)?.data?.detail === 'PASSWORD RESET REQUEST ALREADY USED'
    ) {
      return (
        <Flex
          height='100%'
          flexDirection='column'
          gap={12}
          alignItems='center'
          justifyContent='center'
        >
          <Text variant='h4' fontWeight='fontWeightMedium'>
            <FormattedMessage id='passwordResetRequest.errorRequestExpired' />
          </Text>
          <Button
            variant='primary'
            onClick={() => (window.location.href = '/login')}
            style={{ marginLeft: 8 }}
          >
            <FormattedMessage id='passwordResetRequest.goToLogin' />
          </Button>
        </Flex>
      );
    }
  }

  return (
    <>
      {isRetrying && (
        <MessageContainer gap={14}>
          <Flex flex={0} position='relative'>
            <LocalLoader />
          </Flex>
          <Text variant='h5' fontWeight='fontWeightMedium'>
            <FormattedMessage id='investment.contractSigning.buildingAgreement' />
          </Text>
        </MessageContainer>
      )}
      {showLoader && !isRetrying && <LocalLoader />}
      <>
        <iframe
          src={docusignUrl}
          title='DocuSign'
          style={{ height: '100%', width: '100%', border: 'none' }}
          onLoad={() => setIFrameLoaded(true)}
        />
      </>
    </>
  );
};

export default ExternalContractSigning;
